import { css } from '@emotion/react';
import { Box } from '@spark-web/box';
import { Button } from '@spark-web/button';
import { Container } from '@spark-web/container';
import { Heading } from '@spark-web/heading';
import { Hidden } from '@spark-web/hidden';
import { Row } from '@spark-web/row';
import { Spinner } from '@spark-web/spinner';
import { Stack } from '@spark-web/stack';
import { Text } from '@spark-web/text';
import { useTheme } from '@spark-web/theme';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { PARTNER_ACTION_CARD, PARTNER_WITH_PRICING_DISPLAY } from '~/constants';
import { useVendorsListUrlParams } from '~/hooks/url-params/useVendorsListUrlParams';
import { useMarketplaceVendors } from '~/hooks/useMarketplaceVendors';
import { usePartnerTheming } from '~/hooks/usePartnerTheming';
import { useLeadContext } from '~/provider/LeadProvider';
import { usePromoContext } from '~/provider/PromoProvider';
import {
  HygraphPromotion,
  MarketplaceCategories,
  MarketplaceManufacturerBrands,
  MarketplacePromotion,
  MarketplaceSort,
  MarketplaceVendor,
} from '~/types/marketplace';
import { parseChannel } from '~/utils/channels';
import { formatAddress } from '~/utils/formatter';

import { Tooltip } from '../common/tooltip';
import { PartnerActionCard } from '../partner-action-card';
import { ReviewSelection } from '../quote-review/review-selection';

import { VendorCard } from './components/vendor-card';
import { NeedHelp } from './need-help';
import { FilterDialog, FilterFormData } from './vendors-list/filter-dialog';
import { FiltersBar } from './vendors-list/filters-bar';
import { InterstitialBanner } from './vendors-list/interstitial-banner';
import { PromotionInformation } from './vendors-list/promotion-information';
import { SortDialog } from './vendors-list/sort-dialog';

export type VendorListProps = {
  totalVendorsCount?: number;
  vendors?: Array<MarketplaceVendor>;
  brands?: Array<MarketplaceManufacturerBrands>;
  primaryPromotion: HygraphPromotion | null;
  promotions?: Array<MarketplacePromotion>;
  categories?: Array<MarketplaceCategories>;
};

export const VendorList = ({
  totalVendorsCount,
  vendors: initialVendors,
  brands,
  promotions = [],
  primaryPromotion,
  categories,
}: VendorListProps) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const {
    tooltip: { tone },
  } = usePartnerTheming();

  const { lead } = useLeadContext();

  const { promoCode, setPromoCode } = usePromoContext();

  const {
    synthetic,
    leadId,
    channel,
    partner,
    postcode,
    suburb,
    sort,
    categories: selectedCategories,
    promotions: selectedPromotions,
    manufacturerBrands: selectedManufacturerBrands,
    setQueryParams,
  } = useVendorsListUrlParams({ lead });

  // 1. promotions query params (filtered promotions)
  // 2. lead's selected promotion
  // 3. first from promotions list
  useEffect(() => {
    const currentPromoCode =
      (selectedPromotions?.length > 0 && selectedPromotions[0]) || lead?.promotionCode || primaryPromotion?.codes[0];
    setPromoCode(currentPromoCode);
  }, [selectedPromotions, lead?.promotionCode, primaryPromotion?.codes, setPromoCode]);

  const { data, isLoading, hasNextPage, isFetchingNextPage, status, fetchNextPage } = useMarketplaceVendors({
    initialData:
      initialVendors && totalVendorsCount
        ? {
            vendors: initialVendors,
            count: totalVendorsCount,
          }
        : undefined,
    params: {
      postcode,
      categories: selectedCategories,
      promotions: selectedPromotions,
      brands: selectedManufacturerBrands,
      channel,
      sort,
      leadId,
      synthetic,
    },
  });
  const vendors = data?.flatMap((page) => page.vendors) ?? initialVendors;
  const count = data?.[0]?.count ?? totalVendorsCount;

  const firstVendors = vendors?.slice(0, 2);
  const secondVendors = vendors?.slice(2, 4);
  const restVendors = vendors?.slice(4, vendors?.length);

  const isPartnerHaveAction = partner && partner in PARTNER_ACTION_CARD;
  const isPricingVisible = partner ? PARTNER_WITH_PRICING_DISPLAY.includes(partner) : false;

  const onFilter = ({ postcode: postcodeData, ...data }: FilterFormData) => {
    setQueryParams(
      {
        ...data,
        postcode: postcodeData?.postCode ?? '',
        suburb: postcodeData?.suburb ?? '',
      },
      {
        history: 'push',
      },
    );
  };

  const onSort = (sortType: MarketplaceSort) => {
    setQueryParams(
      (current) => ({
        ...current,
        sort: sortType,
      }),
      {
        history: 'push',
      },
    );
  };

  const onRemovePromotion = (promotionCode: string) => {
    setQueryParams(
      (current) => ({
        ...current,
        promotions: current.promotions?.filter((code) => code !== promotionCode) ?? [],
      }),
      {
        history: 'push',
      },
    );
  };

  if (isLoading) {
    return (
      <Box padding="xlarge">
        <Container size="large">
          <Stack gap="large" align="center" padding="large">
            <Spinner tone="primary" />
            <Text weight="semibold">Finding vendors near you</Text>
          </Stack>
        </Container>
      </Box>
    );
  }

  return (
    <>
      <Stack background="surface" padding="large">
        <Container size="large">
          <Box
            display="flex"
            flexDirection={{ mobile: 'column', tablet: 'row' }}
            gap="large"
            alignItems={{ tablet: 'center' }}
          >
            <Box display="flex" flexDirection="row" justifyContent="start" gap="medium">
              <Box flex={1}>
                <FilterDialog
                  channel={channel}
                  partner={partner}
                  categories={categories}
                  promotions={promotions}
                  manufacturerBrands={brands}
                  initialValues={{
                    postcode: postcode
                      ? {
                          postCode: postcode,
                          suburb: suburb ?? lead?.city?.toUpperCase() ?? '',
                          state: lead?.state ?? '',
                        }
                      : undefined,
                    categories: selectedCategories,
                    promotions: selectedPromotions,
                    manufacturerBrands: selectedManufacturerBrands,
                  }}
                  status={status}
                  onFilter={onFilter}
                />
              </Box>
              <Box flex={1}>
                <SortDialog initialValue={sort} partner={partner} onSort={onSort} />
              </Box>
            </Box>
            {partner && isPricingVisible ? (
              <Tooltip title="Prices are subject to a vendor site visit">
                <Stack gap="xlarge">
                  <Text size="xsmall" tone={tone}>
                    The price guideline is indicative only for completion of the work.
                  </Text>
                  <Text size="xsmall" tone={tone}>
                    The pricing reflects considerations for government rebates, and covers all costs from quoting,
                    installation and purchase of equipment.
                  </Text>
                </Stack>
              </Tooltip>
            ) : null}
          </Box>
        </Container>
      </Stack>
      <Stack gap="large" paddingY="xlarge" paddingX="large">
        <Container size="large">
          {vendors && vendors?.length > 0 ? (
            <Stack gap="xlarge">
              {postcode && suburb ? (
                <Heading level="3" as="h2">
                  {t('Showing {{count}} of {{total}} vendors for {{location}}', {
                    count: vendors?.length,
                    total: count,
                    location: formatAddress({ suburb: suburb.charAt(0) + suburb.toLowerCase().slice(1), postcode }),
                  })}
                </Heading>
              ) : (
                <Heading level="3" as="h2">
                  {t('Showing {{count}} of {{total}} vendors', {
                    count: vendors?.length,
                    total: count,
                  })}
                </Heading>
              )}

              {selectedPromotions.length > 0 ? (
                <FiltersBar
                  promotions={promotions}
                  activeFilters={{ promotionCodes: selectedPromotions }}
                  onRemovePromotion={onRemovePromotion}
                />
              ) : null}

              <Row gap="large">
                <Box
                  gap="xlarge"
                  width="full"
                  css={css({
                    display: 'grid',
                    gridTemplateColumns: '1fr',
                    [`@media (min-width: ${theme.breakpoint.tablet}px)`]: {
                      gridTemplateColumns: '1fr 1fr',
                    },
                  })}
                >
                  {firstVendors?.map((vendor, index) => (
                    <VendorCard
                      key={vendor.publicId}
                      vendor={vendor}
                      channel={channel}
                      index={index}
                      promoCode={promoCode}
                    />
                  ))}
                  <InterstitialBanner channel={parseChannel(channel)} code={promoCode} />
                  {secondVendors?.map((vendor, index) => (
                    <VendorCard
                      key={vendor.publicId}
                      vendor={vendor}
                      channel={channel}
                      index={index + 2}
                      promoCode={promoCode}
                    />
                  ))}
                  {isPartnerHaveAction ? <PartnerActionCard partner={partner} /> : null}
                  {restVendors?.map((vendor, index) => (
                    <VendorCard
                      key={vendor.publicId}
                      vendor={vendor}
                      channel={channel}
                      index={index + 4}
                      promoCode={promoCode}
                    />
                  ))}
                  {hasNextPage ? (
                    <Box
                      css={css({
                        gridColumn: '1 / -1',
                        display: 'flex',
                        justifyContent: 'center',
                        [`@media (max-width: ${theme.breakpoint.tablet - 1}px)`]: {
                          gridColumn: '1',
                        },
                      })}
                      paddingBottom="xxlarge"
                    >
                      <Button tone="neutral" onClick={() => fetchNextPage()} loading={isFetchingNextPage}>
                        Load more
                      </Button>
                    </Box>
                  ) : null}
                </Box>

                <Hidden below="desktop">
                  <ReviewSelection channel={channel} />
                </Hidden>
              </Row>
            </Stack>
          ) : (
            <Stack gap="large">
              <Stack height="large" align="center">
                <Text weight="semibold">No vendors found in your area</Text>
              </Stack>
              <NeedHelp />
              {isPartnerHaveAction ? <PartnerActionCard partner={partner} /> : null}
            </Stack>
          )}

          <PromotionInformation channel={channel} code={promoCode} />
        </Container>
      </Stack>
    </>
  );
};
